/**
 * Login. Mirrors `bimgo-ai-web/client/src/pages/Login.tsx` — both the mechanism
 * and the look — adapted to 2.5's stack (no wouter or shadcn; i18n via
 * `useLocale`/`strings.ts`; styling in `ui/brand.css`; sign-in goes through
 * `AuthProvider` rather than the Supabase client directly).
 *
 * Carried across from bimgo-ai-web:
 *  - **Remember me** — the email is saved to `localStorage` and pre-filled on
 *    return, exactly as the web client does (`bimgo_user_credential`).
 *  - **Inline forgot-password** — a toggle within the same card, with a "check
 *    your email" confirmation and a way back, not a separate route.
 *  - **The signed-out banner** — amber, read once from the reason the
 *    single-session guard leaves in `sessionStorage` and then cleared, so it shows
 *    on the redirect that follows a revocation and not forever after. It has two
 *    wordings: "signed in on another device" only when a peer announcement proved
 *    that, and a neutral "your session has ended" otherwise (#124) — GoTrue's
 *    error codes cannot tell the two apart on their own.
 *
 * Dropped as web-only: the `source=revit-plugin` / `redirect` query handling and
 * wouter navigation — this SPA has one mode and App swaps the page on `session`.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { supabase } from "../lib/supabaseClient";
import { PASSWORD_RESET_REDIRECT } from "../lib/config";
import { SIGNED_OUT_ANOTHER_DEVICE, SIGNED_OUT_REASON_KEY, SIGNED_OUT_SESSION_ENDED } from "../lib/authSession";
import { useLocale } from "../i18n/LocaleProvider";
import "../ui/brand.css";

const REMEMBERED_EMAIL_KEY = "bimgo_user_credential";

export default function Login() {
  const { signIn } = useAuth();
  const { t } = useLocale();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);

  // The single-session guard leaves this behind when GoTrue rejects the session.
  // Read once and clear, so it shows on the redirect that follows and not on every
  // later visit. Two reasons, because only one of them is ever proven: the guard
  // writes `another_device` only when a peer announcement said so, and
  // `session_ended` when the session merely went away (#124).
  useEffect(() => {
    const stored = sessionStorage.getItem(SIGNED_OUT_REASON_KEY);
    if (stored !== SIGNED_OUT_ANOTHER_DEVICE && stored !== SIGNED_OUT_SESSION_ENDED) return;
    sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
    setSignedOutReason(stored);
  }, []);

  // Pre-fill a remembered email.
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (!saved) return;
    setEmail(saved);
    setRememberMe(true);
  }, []);

  async function onLogin(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    // Dismiss whichever signed-out banner was showing: attempting a sign-in is the
    // user acting on it, and leaving it above a fresh error reads as two failures.
    setSignedOutReason(null);

    // On success `session` flips in AuthProvider and App swaps this page for the
    // shell — there is nothing to navigate to by hand.
    const message = await signIn(email, password);
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    if (rememberMe) localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  }

  async function onForgot(event: FormEvent) {
    event.preventDefault();
    setForgotBusy(true);
    setError(null);

    try {
      // **`redirectTo` is not optional here.** Without it GoTrue falls back to
      // the project's Site URL, which is one value for every build that shares
      // the Supabase project — so a dev tester's reset link would land on
      // whichever environment happened to be configured as the site, not the one
      // they asked from. The origin is the build's own, so each environment
      // sends its own link (#21).
      const { error: resetError } = await supabase().auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: PASSWORD_RESET_REDIRECT(),
      });
      if (resetError) setError(resetError.message);
      else setForgotSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-header">
          <img className="auth-logo" src="/bimgo-logo-horizontal-color.png" alt="Bimgo AI" />
        </div>

        <div className="auth-card">
          {signedOutReason ? (
            <div className="auth-banner" role="status">
              {t(
                signedOutReason === SIGNED_OUT_ANOTHER_DEVICE
                  ? "login.signedOutElsewhere"
                  : "login.sessionEnded",
              )}
            </div>
          ) : null}

          {forgot ? (
            forgotSent ? (
              <div>
                <p className="auth-message is-ok" style={{ marginTop: 0 }}>
                  {t("login.resetSent")}
                </p>
                <div className="auth-link-row">
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setForgot(false);
                      setForgotSent(false);
                      setForgotEmail("");
                    }}
                  >
                    {t("login.backToSignIn")}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={onForgot}>
                <h1 className="auth-title">{t("login.resetTitle")}</h1>
                <p className="auth-subtitle">{t("login.resetSubtitle")}</p>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="forgot-email">
                    {t("login.email")}
                  </label>
                  <input
                    id="forgot-email"
                    className="auth-input"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t("login.emailPlaceholder")}
                    required
                  />
                </div>
                {error ? (
                  <p className="auth-message is-error" role="alert">
                    {error}
                  </p>
                ) : null}
                <div style={{ marginTop: "1rem" }}>
                  <button type="submit" className="auth-button" disabled={forgotBusy}>
                    {forgotBusy ? t("login.sending") : t("login.sendResetLink")}
                  </button>
                </div>
                <div className="auth-link-row">
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setForgot(false);
                      setError(null);
                    }}
                  >
                    {t("login.backToSignIn")}
                  </button>
                </div>
              </form>
            )
          ) : (
            <form onSubmit={onLogin}>
              <div className="auth-field">
                <label className="auth-label" htmlFor="login-email">
                  {t("login.email")}
                </label>
                <input
                  id="login-email"
                  className="auth-input"
                  type="email"
                  autoComplete="username"
                  placeholder={t("login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="login-password">
                  {t("login.password")}
                </label>
                <input
                  id="login-password"
                  className="auth-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t("login.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="auth-remember">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    setRememberMe(e.target.checked);
                    if (!e.target.checked) localStorage.removeItem(REMEMBERED_EMAIL_KEY);
                  }}
                />
                <label htmlFor="remember-me">{t("login.rememberMe")}</label>
              </div>

              {error ? (
                <p className="auth-message is-error" role="alert">
                  {error}
                </p>
              ) : null}

              <button type="submit" className="auth-button" disabled={busy}>
                {busy ? t("login.signingIn") : t("login.signIn")}
              </button>

              <div className="auth-link-row">
                <button
                  type="button"
                  className="auth-link"
                  onClick={() => {
                    setForgot(true);
                    setForgotEmail(email);
                    setError(null);
                  }}
                >
                  {t("login.forgotPassword")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
